import { useCallback, useEffect, useState } from "react";

import { HttpApiService } from "@/shared/services/httpApi.service";

import { ensurePseBanks, type PseBank } from "../services/pseBanks.service";

export { pseBankNameFor } from "../services/pseBanks.service";

export interface UsePseBanksResult {
  /** `null` until the first answer arrives — the field renders its loading placeholder off that. */
  banks: PseBank[] | null;
  error: string | null;
  isLoading: boolean;
  /** Re-requests, bypassing the memo. Wired to the error state's retry so a blip is recoverable in place. */
  retry: () => void;
}

/**
 * The PSE bank directory, memoized by `ensurePseBanks` and shared by every surface that needs it.
 *
 * `enabled` keeps the request lazy: the directory is only needed once the payer has actually chosen PSE, so a
 * Bre-B deposit never pays for it.
 */
export function usePseBanks(enabled: boolean): UsePseBanksResult {
  const [banks, setBanks] = useState<PseBank[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  /** The `reloadToken` whose request has come back. `null` while none has. */
  const [settledToken, setSettledToken] = useState<number | null>(null);

  /**
   * Derived rather than stored, because `enabled` is what turns the request on and it can turn on LATER than
   * the first render — that is the whole point of the flag. `PayinReturnPage` mounts with the directory
   * disabled and enables it once the payin's bank code arrives; a `useState(enabled)` seed reads `false`
   * there and never rises again, so the hook would report "not loading, no banks" for the entire fetch —
   * indistinguishable from a directory that came back empty.
   *
   * Deriving also keeps the property the seed was chosen for: a memo hit resolves in the same tick, so there
   * is no on-then-off flicker and nothing to raise inside the effect (which `set-state-in-effect` forbids).
   */
  const isLoading = enabled && settledToken !== reloadToken;

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    ensurePseBanks(reloadToken > 0)
      .then((next) => {
        if (cancelled) return;
        setBanks(next);
        setError(null);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(HttpApiService.getErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setSettledToken(reloadToken);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, reloadToken]);

  const retry = useCallback(() => {
    setError(null);
    setReloadToken((token) => token + 1);
  }, []);

  return { banks, error, isLoading, retry };
}
